import { Check } from 'lucide-react';
import { useMemo, useState } from 'react';

import { Combobox } from '@apps/web/components/ui/combobox';
import {
    CommandEmpty,
    CommandGroup,
    CommandItem,
} from '@apps/web/components/ui/command';
import { cn } from '@apps/web/lib/utils';
import { useAllAdminOrganizations } from '@apps/web/swr_data_fetching/data_fetching_hooks';

type OrganizationComboboxProps = {
    readonly value?: ReadonlyArray<string>;
    readonly onValueChange: (value: Array<string>) => void;
    readonly placeholder?: string;
    readonly disabled?: boolean;
    readonly includeAllOption?: boolean;
    readonly allOptionLabel?: string;
};

export const ALL_ORGANIZATIONS_VALUE = 'all';

const getOrganizationLabel = (organization: {
    readonly internal_name?: string | null;
    readonly name: string;
}) => {
    const internalName = organization.internal_name?.trim();
    return internalName && internalName.length > 0
        ? internalName
        : organization.name;
};

export function OrganizationCombobox({
    value,
    onValueChange,
    placeholder = 'Select organization...',
    disabled = false,
    includeAllOption = false,
    allOptionLabel = 'All Organizations',
}: OrganizationComboboxProps) {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState('');

    const { organizations, isLoading } = useAllAdminOrganizations({
        search,
        autoLoadPages: true,
    });

    const sortedOrganizations = useMemo(() => {
        const ordered = [...organizations];
        ordered.sort((a, b) =>
            getOrganizationLabel(a).localeCompare(getOrganizationLabel(b)),
        );
        return ordered;
    }, [organizations]);

    const labelMap = useMemo(() => {
        const map = new Map<string, string>();
        sortedOrganizations.forEach((org) => {
            map.set(org.id, getOrganizationLabel(org));
        });
        return map;
    }, [sortedOrganizations]);

    const selectedValues = useMemo(() => value ?? [], [value]);
    const allOrganizationsSelected =
        includeAllOption && selectedValues.includes(ALL_ORGANIZATIONS_VALUE);
    const selectedOrganizationValues = useMemo(
        () => selectedValues.filter((id) => id !== ALL_ORGANIZATIONS_VALUE),
        [selectedValues],
    );
    const selectedIds = useMemo(
        () => new Set(selectedOrganizationValues),
        [selectedOrganizationValues],
    );
    const totalSelected = selectedOrganizationValues.length;
    const selectedLabels = useMemo(() => {
        return selectedOrganizationValues
            .map((id) => labelMap.get(id))
            .filter((label): label is string => Boolean(label));
    }, [labelMap, selectedOrganizationValues]);

    const allLabelsKnown = selectedLabels.length === totalSelected;
    const sortedSelectedLabels = useMemo(() => {
        if (!allLabelsKnown) {
            return [] as Array<string>;
        }
        return [...selectedLabels].sort((a, b) => a.localeCompare(b));
    }, [allLabelsKnown, selectedLabels]);

    const buttonDisplayValue = (() => {
        if (allOrganizationsSelected) {
            return allOptionLabel;
        }
        if (totalSelected === 0) {
            return undefined;
        }
        if (allLabelsKnown && sortedSelectedLabels.length === 1) {
            return sortedSelectedLabels[0];
        }
        if (allLabelsKnown && sortedSelectedLabels.length <= 3) {
            return sortedSelectedLabels.join(', ');
        }
        if (allLabelsKnown && sortedSelectedLabels.length > 3) {
            return `${sortedSelectedLabels[0]} +${totalSelected - 1}`;
        }
        return `${totalSelected} selected`;
    })();

    return (
        <Combobox
            open={open}
            onOpenChange={setOpen}
            displayValue={buttonDisplayValue}
            placeholder={placeholder}
            disabled={disabled || isLoading}
            searchValue={search}
            onSearchValueChange={setSearch}
            searchPlaceholder="Search organizations..."
        >
            <CommandEmpty>
                {isLoading ? 'Loading...' : 'No organizations found.'}
            </CommandEmpty>
            <CommandGroup>
                {includeAllOption && (
                    <CommandItem
                        value={ALL_ORGANIZATIONS_VALUE}
                        onSelect={() => {
                            onValueChange([ALL_ORGANIZATIONS_VALUE]);
                            setOpen(false);
                        }}
                    >
                        <Check
                            className={cn(
                                'mr-2 h-4 w-4',
                                allOrganizationsSelected
                                    ? 'opacity-100'
                                    : 'opacity-0',
                            )}
                        />
                        {allOptionLabel}
                    </CommandItem>
                )}
                <CommandItem
                    value=""
                    onSelect={() => {
                        onValueChange([]);
                        setOpen(false);
                    }}
                >
                    <Check
                        className={cn(
                            'mr-2 h-4 w-4',
                            !allOrganizationsSelected && totalSelected === 0
                                ? 'opacity-100'
                                : 'opacity-0',
                        )}
                    />
                    None
                </CommandItem>
                {sortedOrganizations.map((org) => {
                    const label =
                        labelMap.get(org.id) ?? getOrganizationLabel(org);
                    const internalName = org.internal_name?.trim();
                    const showSecondaryName =
                        internalName &&
                        internalName.length > 0 &&
                        internalName !== org.name;
                    const keywords = [org.name];

                    if (internalName && internalName.length > 0) {
                        keywords.push(internalName);
                    }

                    return (
                        <CommandItem
                            key={org.id}
                            value={org.id}
                            keywords={keywords}
                            onSelect={(currentValue) => {
                                const nextSelected = new Set(selectedIds);
                                if (nextSelected.has(currentValue)) {
                                    nextSelected.delete(currentValue);
                                } else {
                                    nextSelected.add(currentValue);
                                }
                                const nextValues = Array.from(nextSelected);
                                nextValues.sort((a, b) => {
                                    const labelA = labelMap.get(a);
                                    const labelB = labelMap.get(b);
                                    if (labelA && labelB) {
                                        return labelA.localeCompare(labelB);
                                    }
                                    if (labelA) {
                                        return -1;
                                    }
                                    if (labelB) {
                                        return 1;
                                    }
                                    return 0;
                                });
                                onValueChange(nextValues);
                            }}
                        >
                            <Check
                                className={cn(
                                    'mr-2 h-4 w-4',
                                    selectedIds.has(org.id)
                                        ? 'opacity-100'
                                        : 'opacity-0',
                                )}
                            />
                            {/* Wrap rather than truncate: the popover is only
                                as wide as its trigger, and agencies with long
                                or near-identical names are indistinguishable
                                once the label is clipped. */}
                            <div className="flex min-w-0 flex-col">
                                <span className="break-words">{label}</span>
                                {showSecondaryName && (
                                    <span className="text-muted-foreground text-xs break-words">
                                        {org.name}
                                    </span>
                                )}
                            </div>
                        </CommandItem>
                    );
                })}
            </CommandGroup>
        </Combobox>
    );
}
